import type { LocalHorizontal, LocalOrigin } from "../coordinates.ts";
import type { HorizontalBounds, PolygonWithHoles } from "../geometry.ts";

export const WORLD_TILE_SCHEMA_VERSION = 1 as const;
export const WORLD_TILE_SIZE_M = 256 as const;
export const TERRAIN_QUADS_PER_TILE = 16 as const;
export const TERRAIN_SAMPLES_PER_TILE_SIDE = TERRAIN_QUADS_PER_TILE + 1;
export const TERRAIN_SAMPLE_SPACING_M =
  WORLD_TILE_SIZE_M / TERRAIN_QUADS_PER_TILE;

export type WorldTileId = `${number}:${number}`;

export interface WorldTileAddress {
  x: number;
  z: number;
}

export interface TerrainTileData {
  samplesPerSide: number;
  spacingM: number;
  /** Row-major samples. Rows and columns increase with local Z and X. */
  heightsM: readonly number[];
}

export interface BuildingFootprintData {
  id: string;
  sourceFootprintId: string;
  sourceId: string;
  /** Coordinates are metres relative to the owning tile origin. */
  footprint: PolygonWithHoles;
  /** Height above the documented local vertical origin. */
  baseElevationM: number;
  /** Attribution for explicit base elevation; absent for terrain-sampled bases. */
  baseElevationSourceId?: string;
  /** Source-derived height where the fixture includes height evidence. */
  heightM?: number;
  /** Attribution for the source or derivation that supplied heightM. */
  heightSourceId?: string;
  /** Simplified source-mapped roof profile; the GRPK footprint remains authoritative. */
  roofShape?: "flat" | "gable" | "hip" | "mansard";
  /** Attribution for the source that supplied roofShape. */
  roofSourceId?: string;
  /** Source-mapped ridge axis relative to the footprint's longest edge. */
  roofOrientation?: "along" | "across";
  /** Attribution for the evidence that supplied roofOrientation. */
  roofOrientationSourceId?: string;
  /** Source-measured vertical difference between roof eave/minimum and peak. */
  roofRiseM?: number;
}

export interface DevelopmentWorldTile {
  schemaVersion: typeof WORLD_TILE_SCHEMA_VERSION;
  id: WorldTileId;
  address: WorldTileAddress;
  sizeM: typeof WORLD_TILE_SIZE_M;
  origin: LocalHorizontal;
  bounds: HorizontalBounds;
  localOriginLks94: LocalOrigin;
  sourceIds: readonly string[];
  terrain: TerrainTileData;
  buildings: readonly BuildingFootprintData[];
}

export function worldTileId(address: Readonly<WorldTileAddress>): WorldTileId {
  return `${address.x}:${address.z}`;
}

export function parseWorldTileId(id: WorldTileId): WorldTileAddress {
  const [rawX, rawZ] = id.split(":");
  const x = Number(rawX);
  const z = Number(rawZ);
  if (!Number.isSafeInteger(x) || !Number.isSafeInteger(z)) {
    throw new Error(`Invalid world tile ID: ${id}`);
  }
  return { x, z };
}

export function worldTileBounds(
  address: Readonly<WorldTileAddress>,
): HorizontalBounds {
  const minX = address.x * WORLD_TILE_SIZE_M;
  const minZ = address.z * WORLD_TILE_SIZE_M;
  return {
    minX,
    minZ,
    maxX: minX + WORLD_TILE_SIZE_M,
    maxZ: minZ + WORLD_TILE_SIZE_M,
  };
}

export function worldTileAddressAt(
  point: Readonly<LocalHorizontal>,
): WorldTileAddress {
  return {
    x: Math.floor(point.x / WORLD_TILE_SIZE_M),
    z: Math.floor(point.z / WORLD_TILE_SIZE_M),
  };
}

export function tileRelativePoint(
  point: Readonly<LocalHorizontal>,
  tileOrigin: Readonly<LocalHorizontal>,
): LocalHorizontal {
  return {
    x: point.x - tileOrigin.x,
    z: point.z - tileOrigin.z,
  };
}

export function tileWorldPoint(
  point: Readonly<LocalHorizontal>,
  tileOrigin: Readonly<LocalHorizontal>,
): LocalHorizontal {
  return {
    x: point.x + tileOrigin.x,
    z: point.z + tileOrigin.z,
  };
}
